import { getData } from '../CommonElements/Functions/PostGetFun';
import { API } from '../api/API';
import { getSystemConfig } from './SystemConfig';

export interface LocationCoords {
    latitude: number;
    longitude: number;
}

export interface GeofencingCheckResult {
    allowed: boolean;
    locationPromptRequired?: boolean;
    gpsNotFound?: boolean;
    approvalFlow?: boolean;
    geoOverrideAvailable?: boolean;
    message?: string;
    coords?: LocationCoords;
}

/**
 * Get current user position via HTML5 Geolocation API
 */
export const getCurrentWebLocation = (): Promise<LocationCoords | null> => {
    return new Promise((resolve) => {
        if (!navigator.geolocation) {
            resolve(null);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                if (pos && pos.coords) {
                    resolve({
                        latitude: pos.coords.latitude,
                        longitude: pos.coords.longitude,
                    });
                } else {
                    resolve(null);
                }
            },
            (error) => {
                console.warn("Error getting browser location:", error);
                resolve(null);
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
        );
    });
};

/**
 * Perform Geofencing and Location validation for DCR / Outlet Reporting
 */
export const validateGeofencingForDCR = async (
    outletOrgId: number | string,
    overrideGps = false
): Promise<GeofencingCheckResult> => {
    const serverUrl = localStorage.getItem("serverUrl");
    const captureGeoLocation = String(getSystemConfig("capture_geo_location") || "").trim().toLowerCase();
    const rawGeoVal = String(getSystemConfig("enable_geo_fencing") || "").trim().toLowerCase();
    const enableGeoFencing = rawGeoVal === "yes" || rawGeoVal === "on" || rawGeoVal === "true" || getSystemConfig("enable_geo_fencing") === 1;

    const rawOverrideVal = String(getSystemConfig("outlet_lat_lng_override") || "").trim().toLowerCase();
    const isOutletLatlngOverride = rawOverrideVal === "yes" || rawOverrideVal === "on" || rawOverrideVal === "true" || getSystemConfig("outlet_lat_lng_override") === 1;

    const isGeoForce = captureGeoLocation === "force" || captureGeoLocation.includes("dcr") || enableGeoFencing;

    // 1. Fetch current web location
    const coords = await getCurrentWebLocation();

    if (isGeoForce && (!coords || !coords.latitude || !coords.longitude)) {
        return {
            allowed: false,
            locationPromptRequired: true,
            message: "Browser location access is required to report DCR. Please enable location permissions and retry.",
        };
    }

    // 2. If Geofencing is enabled, call API.geofencing validation endpoint
    if (enableGeoFencing && coords && serverUrl) {
        try {
            const url = `${serverUrl}${API.geofencing}`;
            const validOutletOrgId = outletOrgId && outletOrgId !== "undefined" && outletOrgId !== "null" ? outletOrgId : "";
            const params: any = {
                latlng: `${coords.latitude},${coords.longitude}`,
            };
            if (validOutletOrgId) {
                params.outlet_org_id = validOutletOrgId;
            }

            if (overrideGps) {
                params.outlet_latlng_override = "yes";
            }

            const response = await getData(url, true, params);
            console.log("[GeofencingHelper] API response:", response);

            const resData = response.data;
            const statusCode = Number(response.status || resData?.statusCode);

            const isApprovalFlow =
                resData?.message === "Data is going for approval flow." ||
                String(resData?.message || "").toLowerCase().includes("approval") ||
                resData?.data?.ModuleName === "geo_fencing";

            if (statusCode === 200) {
                return {
                    allowed: true,
                    message: resData?.message || "Location verified successfully!",
                    coords,
                };
            } else if (
                statusCode === 400 &&
                (resData?.message === "Outlet GPS Data Not Found." || resData?.error === "Outlet GPS Data Not Found.")
            ) {
                return {
                    allowed: false,
                    gpsNotFound: true,
                    message: resData?.message || "Outlet GPS Data Not Found. Do you want to set current location as Outlet GPS?",
                    coords,
                };
            } else if (isApprovalFlow) {
                return {
                    allowed: false,
                    approvalFlow: true,
                    message: resData?.message || "Data is going for approval flow.",
                    coords,
                };
            } else {
                return {
                    allowed: false,
                    geoOverrideAvailable: isOutletLatlngOverride,
                    message: resData?.message || resData?.error || "You are outside the assigned geofence for this outlet.",
                    coords,
                };
            }
        } catch (error: any) {
            console.error("[GeofencingHelper] Error validating geofencing:", error);
            return {
                allowed: false,
                message: error?.message || "Unable to verify location geofencing. Please check network and retry.",
                coords: coords || undefined,
            };
        }
    }

    return {
        allowed: true,
        coords: coords || undefined,
    };
};
